// The home's count reads (D-443 ruling 2). Each is ONE page of a list, and the
// count it yields is one of the three a page can know (routes/shell-data.ts):
// exact, a true lower bound ("50+") when the page did not finish the list, or
// unknown when the read failed. Every read is enabled only for a reader who
// holds the code its own endpoint demands, so no home sends a request that can
// only answer 403 (D-040, D-118), and every read reports when it has settled so
// the screen can reveal once (§5).

import { useQuery } from "@tanstack/react-query";
import { client } from "../../../lib/api";
import { profileReadEpoch } from "../../../lib/profile-locale";
import {
  COUNT_UNKNOWN,
  countFromPage,
  type ShellCount,
  type ShellCountRead,
} from "../../../routes/shell-data";

const STALE_MS = 60_000;

/** One page is enough: a count past fifty is a lower bound, and fifty things
 * waiting is the same instruction to the reader as fifty-one. */
export const HOME_COUNT_LIMIT = 50;

/** What a count read's fetch hands back: one page of any list. */
export interface PageLike {
  readonly items?: readonly unknown[] | undefined;
  readonly next_cursor?: string | null | undefined;
}

/** A count read with the screen's settle flag. `enabled: false` settles at
 * once, because a read never sent must never hold the skeleton. */
export function useHomeCount(
  key: string,
  enabled: boolean,
  fetchPage: (signal: AbortSignal) => Promise<PageLike | undefined>,
): ShellCountRead {
  const q = useQuery({
    queryKey: ["home", "count", key],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => (await fetchPage(signal)) ?? null,
  });
  const body = q.data ?? undefined;
  return {
    count: body === undefined ? COUNT_UNKNOWN : countFromPage(body.items, body.next_cursor),
    isSettled: !enabled || q.isFetched,
    updatedAt: q.dataUpdatedAt,
  };
}

/** An exact number from a read that answers integers (a summary), as a count. */
export function exactCount(n: number | undefined): ShellCount {
  return n === undefined ? COUNT_UNKNOWN : { kind: "exact", n };
}

/** The signed-in user's id, for "mine" filters (`GET /v1/auth/profile`).
 * Keyed on the sign-in epoch: sign-in is a client-side navigation and logout
 * keeps the query cache, so a key without it handed the previous person's id
 * to the next one on a shared machine for as long as the cache held it. */
export function useMyUserId(enabled: boolean): { id: string | undefined; isSettled: boolean } {
  const q = useQuery({
    queryKey: ["home", "profile", profileReadEpoch()],
    enabled,
    retry: false,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/auth/profile", { signal });
      return data ?? null;
    },
  });
  return { id: q.data?.user_id ?? undefined, isSettled: !enabled || q.isFetched };
}

/** True when a count says something is waiting, so its row takes a tone. */
export function hasAny(count: ShellCount): boolean {
  return count.kind !== "unknown" && count.n > 0;
}
